import { useMemo, useState } from 'react';
import { List, Pencil, Plus, Trash2 } from 'lucide-react';
import { readSettingListItem, settingListItemTypeSpec, type AppSettingList } from '@peles/shared';
import { EntityList, type EntityListColumn } from '../../common/EntityList';
import { SettingToggle } from '../SettingToggle';
import { useDialog } from '../../../context/DialogContext';
import { useDeleteAppSettingList, useUpdateAppSettingList } from '../../../hooks/useAppSettings';
import { SettingListModal } from './SettingListModal';

export interface SettingListsSectionProps {
  lists: AppSettingList[];
  isLoading?: boolean;
}

const PAGE_SIZE = 10;
const PREVIEW = 4;

/** What a list's first few items are called, for its row. */
function itemPreview(list: AppSettingList): { names: string[]; more: number } {
  const items = Array.isArray(list.items) ? list.items : [];
  const names = items
    .slice(0, PREVIEW)
    .map((raw) => {
      const d = readSettingListItem(raw);
      return d.label || d.value;
    })
    .filter(Boolean);
  return { names, more: Math.max(0, items.length - PREVIEW) };
}

/**
 * The office's settings lists — chair types, guest tags, booking stages… —
 * on the App Settings tab beside the settings that use them. A list opens in
 * `SettingListModal`, where its items are added and edited one by one.
 */
export function SettingListsSection({ lists, isLoading }: SettingListsSectionProps) {
  const { confirm } = useDialog();
  const updateList = useUpdateAppSettingList();
  const deleteList = useDeleteAppSettingList();
  const [open, setOpen] = useState<{ list: AppSettingList | null } | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...lists].sort((a, b) => a.name.localeCompare(b.name));
    if (!q) return sorted;
    return sorted.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.listCode.toLowerCase().includes(q) ||
        (l.description ?? '').toLowerCase().includes(q),
    );
  }, [lists, search]);
  const lastPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shownPage = Math.min(page, lastPage);
  const pageItems = filtered.slice((shownPage - 1) * PAGE_SIZE, shownPage * PAGE_SIZE);

  async function handleDelete(list: AppSettingList) {
    if (
      !(await confirm({
        title: 'Delete list',
        message: `Delete the list "${list.name}"? Screens that offer its items will have nothing to offer.`,
        confirmLabel: 'Delete',
      }))
    )
      return;
    await deleteList.mutateAsync(list.id);
  }

  const columns: EntityListColumn<AppSettingList>[] = [
    {
      key: 'name',
      header: 'List',
      render: (l) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900">{l.name}</div>
          <div className="text-xs font-mono text-gray-500">{l.listCode}</div>
        </div>
      ),
    },
    {
      key: 'itemType',
      header: 'Item type',
      render: (l) => (
        <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
          {settingListItemTypeSpec(l.itemType).label}
        </span>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      hideOnMobile: true,
      render: (l) => {
        const { names, more } = itemPreview(l);
        if (names.length === 0) return <span className="text-xs text-gray-400">No items</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {names.map((n, i) => (
              <span
                key={`${n}-${i}`}
                className="px-2 py-0.5 text-xs rounded-full bg-primary-50 text-primary-700"
              >
                {n}
              </span>
            ))}
            {more > 0 && <span className="px-1 text-xs text-gray-500">+{more} more</span>}
          </div>
        );
      },
    },
    {
      key: 'active',
      header: 'Active',
      align: 'center',
      render: (l) => (
        <span onClick={(e) => e.stopPropagation()}>
          <SettingToggle
            checked={l.isActive}
            onChange={(next) => updateList.mutate({ id: l.id, updates: { isActive: next } })}
            label={l.isActive ? `Deactivate ${l.name}` : `Activate ${l.name}`}
          />
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (l) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setOpen({ list: l })}
            className="inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 rounded text-gray-400 hover:text-primary-600 hover:bg-gray-100"
            aria-label={`Edit ${l.name}`}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={() => void handleDelete(l)}
            className="inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 rounded text-gray-400 hover:text-red-600 hover:bg-gray-100"
            aria-label={`Delete ${l.name}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <section className="p-4 border-t border-gray-200" aria-labelledby="setting-lists-heading">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <List size={16} className="text-primary-600" />
            <h3 id="setting-lists-heading" className="text-sm font-semibold text-gray-900">
              Lists
            </h3>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            The choices screens offer — chair types, guest tags, booking stages and more.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen({ list: null })}
          className="inline-flex shrink-0 items-center gap-1 min-h-11 sm:min-h-0 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700"
        >
          <Plus size={14} />
          New list
        </button>
      </div>

      <EntityList
        items={pageItems}
        columns={columns}
        getKey={(l) => l.id}
        isLoading={isLoading}
        onRowClick={(l) => setOpen({ list: l })}
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Search lists…"
        page={shownPage}
        pageSize={PAGE_SIZE}
        total={filtered.length}
        onPageChange={setPage}
        emptyMessage={search ? 'No list matches.' : 'No lists yet.'}
      />

      {open && (
        <SettingListModal
          list={open.list}
          existingCodes={lists.map((l) => l.listCode)}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}
